import { createRefreshSessionStorage } from './refreshSessionStorage';

const ACCESS_TOKEN_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refreshToken';
const REMEMBER_ME_KEY = 'rememberMe';
const USERNAME_KEY = 'userEmail';

type TokenUpdateDetail = {
  idToken?: string;
  refreshToken?: string;
  remember?: boolean;
  username?: string;
};

const isBrowser = () => typeof window !== 'undefined';

const dispatchTokenEvent = (detail: TokenUpdateDetail) => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent('orgcloud:token-updated', { detail }));
};

const extractMessage = (errorData: any): string => {
  if (!errorData) return '';
  if (typeof errorData === 'string') return errorData;
  return errorData.message || errorData.error || errorData.errorMessage || '';
};

class TokenManager {
  private static sessionRevision = 0;
  private static identityRevision = 0;
  private static renewedTokens = new Map<string, string>();
  private static pendingRefresh: { isCurrent: () => boolean; promise: Promise<string> } | null = null;

  static isSessionChangedError(error: unknown): boolean {
    return error instanceof Error && error.name === 'SessionChangedError';
  }

  static sessionChangedError(): Error {
    return Object.assign(new Error('Session changed during refresh'), { name: 'SessionChangedError' });
  }

  // Only tokens actually renewed by this manager belong to the current lineage.
  // Never infer identity equivalence from unsigned JWT claims or an email alone.
  static captureRequestSession(token: string): () => boolean {
    const revision = this.identityRevision;
    return () => !!token && revision === this.identityRevision &&
      (token === this.getIdToken() || this.renewedTokens.get(token) === this.getIdToken());
  }

  static getIdToken(): string {
    if (!isBrowser()) return '';
    return localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY) || '';
  }

  static getRefreshToken(): string {
    if (!isBrowser()) return '';
    return localStorage.getItem(REFRESH_TOKEN_KEY) || '';
  }

  static getRememberMe(): boolean {
    if (!isBrowser()) return false;
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  }

  static getUsername(): string {
    if (!isBrowser()) return '';
    return localStorage.getItem(USERNAME_KEY) || '';
  }

  static storeSession(detail: TokenUpdateDetail) {
    this.identityRevision += 1;
    this.renewedTokens.clear();
    this.persistSession(detail);
  }

  private static persistSession(detail: TokenUpdateDetail) {
    if (!isBrowser()) return;
    this.sessionRevision += 1;
    const remember = detail.remember ?? this.getRememberMe();
    if (detail.idToken !== undefined) {
      const persistentStorage = remember ? localStorage : sessionStorage;
      const transientStorage = remember ? sessionStorage : localStorage;
      if (detail.idToken) {
        persistentStorage.setItem(ACCESS_TOKEN_KEY, detail.idToken);
      } else {
        persistentStorage.removeItem(ACCESS_TOKEN_KEY);
      }
      transientStorage.removeItem(ACCESS_TOKEN_KEY);
    }
    if (detail.refreshToken !== undefined) {
      if (detail.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, detail.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    }
    if (detail.remember !== undefined) {
      localStorage.setItem(REMEMBER_ME_KEY, detail.remember ? 'true' : 'false');
    }
    if (detail.username !== undefined) {
      if (detail.username) {
        localStorage.setItem(USERNAME_KEY, detail.username);
      } else {
        localStorage.removeItem(USERNAME_KEY);
      }
    }
    dispatchTokenEvent(detail);
  }

  static clearSession() {
    if (!isBrowser()) return;
    this.sessionRevision += 1;
    this.identityRevision += 1;
    this.renewedTokens.clear();
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    localStorage.removeItem(USERNAME_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REMEMBER_ME_KEY);
    sessionStorage.removeItem(USERNAME_KEY);
    dispatchTokenEvent({ idToken: '', refreshToken: '', remember: false, username: '' });
  }

  static shouldAttemptRefresh(): boolean {
    return this.getRememberMe() && !!this.getRefreshToken() && !!this.getUsername();
  }

  static isTokenError(message?: string): boolean {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return (
      normalized.includes('token') ||
      normalized.includes('expired') ||
      normalized.includes('signature')
    );
  }

  static async refreshSession(): Promise<string> {
    if (!this.shouldAttemptRefresh()) {
      throw new Error('Refresh token not available');
    }

    if (this.pendingRefresh?.isCurrent()) return this.pendingRefresh.promise;

    const refreshTokenValue = this.getRefreshToken();
    const username = this.getUsername();
    const idToken = this.getIdToken();
    const revision = this.sessionRevision;
    const isCurrent = () => this.sessionRevision === revision &&
      this.shouldAttemptRefresh() && this.getUsername() === username &&
      this.getRefreshToken() === refreshTokenValue && this.getIdToken() === idToken;
    const assertCurrent = () => {
      if (!isCurrent()) {
        throw this.sessionChangedError();
      }
    };

    const promise = (async () => {
      let cache: ReturnType<typeof createRefreshSessionStorage> | undefined;
      try {
        // Public visitors do not need the authentication SDK. Keep both imports
        // behind the eligibility check, without caching a failed chunk download.
        const [{ CognitoRefreshToken, CognitoUser }, { default: userpool }] = await Promise.all([
          import('amazon-cognito-identity-js'),
          import('../cognito/userpool'),
        ]);
        assertCurrent();
        cache = createRefreshSessionStorage(localStorage, userpool.getClientId(), username, refreshTokenValue, isCurrent);
        const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue });
        const user = new CognitoUser({ Username: username, Pool: userpool, Storage: cache.storage });

        const session = await new Promise<{ idToken: string; refreshToken: string }>((resolve, reject) => {
          user.refreshSession(refreshToken, (err, result) => {
            if (err) {
              reject(err);
              return;
            }

            const newIdToken = result.getIdToken().getJwtToken();
            const newRefreshToken = result.getRefreshToken().getToken() || refreshTokenValue;
            resolve({ idToken: newIdToken, refreshToken: newRefreshToken });
          });
        });

        assertCurrent();
        if (!cache.commit()) throw this.sessionChangedError();
        this.renewedTokens.forEach((_value, token) => this.renewedTokens.set(token, session.idToken));
        this.renewedTokens.set(idToken, session.idToken);
        // Bound memory; very old requests fail closed instead of changing identity.
        if (this.renewedTokens.size > 32) this.renewedTokens.delete(this.renewedTokens.keys().next().value);
        this.persistSession({ idToken: session.idToken, refreshToken: session.refreshToken, remember: true, username });
        return session.idToken;
      } catch (error) {
        // Classify late failures too: callers must not clear a newer session.
        assertCurrent();
        if (!this.isSessionChangedError(error) && cache && !cache.commit()) throw this.sessionChangedError();
        throw error;
      }
    })();
    this.pendingRefresh = { isCurrent, promise };
    try {
      return await promise;
    } finally {
      if (this.pendingRefresh?.promise === promise) this.pendingRefresh = null;
    }
  }

  static redirectToLogin() {
    if (!isBrowser()) return;
    const pathname = window.location.pathname;
    // No redirigir si estamos en una ruta pública o cualquier ruta de auth
    // (sign-in, sign-up, verification, etc.)
    if (
      pathname.startsWith('/public/') ||
      pathname.startsWith('/event/') ||
      pathname.startsWith('/e/') ||
      pathname.startsWith('/competition/') ||
      pathname.startsWith('/c/') ||
      pathname.startsWith('/r/') ||
      pathname.startsWith('/profile/') ||
      pathname.startsWith('/auth/')
    ) {
      return;
    }
    window.location.replace('/auth/sign-in/');
  }

  static extractErrorMessage(error: any): string {
    if (!error) return '';
    if (error.response) {
      if (error.response.data) {
        return extractMessage(error.response.data);
      }
      if (error.response.statusText) {
        return error.response.statusText;
      }
    }
    return error.message || '';
  }
}

export default TokenManager;
